import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { actions } from "app/configSlice";
import I18n from "common/i18n";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { CloseBtn, PlanBadge, FadeTransition, DialogMessage } from "common/elements";
import { PlanType, usePlan } from "features/subscription";
import { ParentGate, ParentWarningTitle } from "features/parent";

const GRID_COLUMNS = "20% 20% 20% 20% 20%";

const PLAN_COLORS: Record<PlanType, string> = {
  [PlanType.TRIAL]: "#9E9E9E",
  [PlanType.FREE]: "#4C9DF1",
  [PlanType.STANDARD]: "#66BB6A",
  [PlanType.PREMIUM]: "#FF5722",
  [PlanType.BUILDER]: "#9C27B0",
  [PlanType.EDUCATION]: "#3E5878",
};

const ComparisonRow = ({
  icon,
  label,
  trialValue,
  freeValue,
  standardValue,
  premiumValue,
  displayPlan,
  onPlanClick,
}: {
  icon: string;
  label: string;
  trialValue: string;
  freeValue: string;
  standardValue: string;
  premiumValue: string;
  displayPlan: PlanType;
  onPlanClick: (planType: PlanType) => void;
}) => {
  const currentColor = PLAN_COLORS[displayPlan];

  const getColumnStyle = (planType: PlanType) => {
    const isSelected = displayPlan === planType;
    return {
      className: `flex items-center justify-center p-3 text text-center text-base border-l-4 border-r-4 cursor-pointer ${
        isSelected ? "text-textcolor-black" : "text-gray2-100 hover:bg-gray-50"
      }`,
      style: {
        borderLeftColor: isSelected ? currentColor : "transparent",
        borderRightColor: isSelected ? currentColor : "transparent",
        backgroundColor: isSelected ? `${currentColor}10` : undefined,
      },
    };
  };

  const trialStyle = getColumnStyle(PlanType.TRIAL);
  const freeStyle = getColumnStyle(PlanType.FREE);
  const standardStyle = getColumnStyle(PlanType.STANDARD);
  const premiumStyle = getColumnStyle(PlanType.PREMIUM);

  return (
    <div
      className="grid border-b"
      style={{
        gridTemplateColumns: GRID_COLUMNS,
        borderBottomColor: "#E8DCC8",
      }}
    >
      <div className="flex items-center gap-2 p-3 text text-textcolor-black">
        <span className="material-symbols-outlined !text-xl text-gray2-100">{icon}</span>
        <span className="text-xl">{label}</span>
      </div>
      <div
        className={trialStyle.className}
        style={trialStyle.style}
        onClick={() => onPlanClick(PlanType.TRIAL)}
      >
        {trialValue}
      </div>
      <div
        className={freeStyle.className}
        style={freeStyle.style}
        onClick={() => onPlanClick(PlanType.FREE)}
      >
        {freeValue}
      </div>
      <div
        className={standardStyle.className}
        style={standardStyle.style}
        onClick={() => onPlanClick(PlanType.STANDARD)}
      >
        {standardValue}
      </div>
      <div
        className={premiumStyle.className}
        style={premiumStyle.style}
        onClick={() => onPlanClick(PlanType.PREMIUM)}
      >
        {premiumValue}
      </div>
    </div>
  );
};

export const UpgradePromotion = () => {
  const PANEL_WIDTH = 1180;
  const PANEL_HEIGHT = 900;
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleDialog = useDialog();
  const [parentGate, setParentGate] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "register" | "purchase" | "register_from_upgrade" | null
  >(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const upgradePromotion = useSelector((state: RootState) => state.config.dialog.upgrade_promotion);
  const parent = useSelector((state: RootState) => state.user.parent);
  const plan = usePlan();

  // 表示用のプラン（選択されていれば選択プラン、なければ現在のプラン）
  const displayPlan = selectedPlan ?? plan;

  const isLoggedIn = parent && parent.account_type !== "guest";

  // UpgradePromotion表示時に前の警告Dialogを閉じる＆選択プランをリセット
  useEffect(() => {
    if (upgradePromotion.value) {
      handleDialog({ type: DialogType.RESTRICTED_FEATURE, value: false });
      setSelectedPlan(null);
    }
  }, [upgradePromotion.value]);

  // プランエリアをクリックしたときの処理
  const handlePlanClick = (planType: PlanType) => {
    play();
    setSelectedPlan(planType);
  };

  const handleClose = () => {
    btnDelay(() => {
      play();
      handleDialog({ type: DialogType.UPGRADE_PROMOTION, value: false });
    });
  };

  // FREE列の「登録」ボタン
  const handleRegisterClick = () => {
    btnDelay(() => {
      play();
      setPendingAction("register");
      setParentGate(true);
    });
  };

  // STANDARD/PREMIUM列の「アップグレード」ボタン
  const handlePurchaseClick = () => {
    btnDelay(() => {
      play();
      if (!isLoggedIn) {
        // 未ログイン → 先にログイン促進Dialogを表示
        setShowLoginDialog(true);
      } else {
        // ログイン済み → ParentGateを表示
        setPendingAction("purchase");
        setParentGate(true);
      }
    });
  };

  const handleParentGateClose = () => {
    setParentGate(false);
    setPendingAction(null);
  };

  const handleParentGateConfirm = () => {
    setParentGate(false);

    if (pendingAction === "register") {
      // FREE列の登録ボタン → 登録ページへ
      dispatch(actions.clearDialog());
      navigate("/auth/register");
    } else if (pendingAction === "register_from_upgrade") {
      // アップグレード → ログイン促進Dialog → 新規登録 → 登録ページへ
      dispatch(actions.clearDialog());
      navigate("/auth/register");
    } else if (pendingAction === "purchase") {
      // ログイン済みユーザーのアップグレード → 課金ページへ
      dispatch(actions.clearDialog());
      navigate("/parent?tab=subscription");
    }
    setPendingAction(null);
  };

  const handleLoginDialogClose = () => {
    setShowLoginDialog(false);
  };

  const handleLoginDialogConfirm = () => {
    setShowLoginDialog(false);
    // 新規登録ボタン押下 → ParentGateを表示
    setPendingAction("register_from_upgrade");
    setParentGate(true);
  };

  return (
    <>
      <Dialog
        fullScreen
        maxWidth={false}
        sx={{ zIndex: 999999999 }}
        open={upgradePromotion.value}
        TransitionComponent={FadeTransition}
        componentsProps={{
          backdrop: { style: { backgroundColor: "transparent" } },
        }}
        PaperProps={{
          style: {
            margin: 0,
            overflow: "visible",
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
      >
        <div
          className="flex-col-el flex-center relative w-full h-full"
          style={{
            filter:
              "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
          }}
        >
          <div
            className="flex-col-el flex-center"
            style={{
              width: PANEL_WIDTH * scale,
              height: PANEL_HEIGHT * scale,
            }}
          >
            <div
              className="flex-col-el flex-center origin-center"
              style={{
                width: PANEL_WIDTH,
                height: PANEL_HEIGHT,
                transform: `scale(${scale})`,
                marginTop: 32 * scale,
              }}
            >
              <CloseBtn close={handleClose} />
              <div className="flex-col-el flex-center w-full h-full rounded-[8px] bg-beige-60 p-6 shadow-[0_12px_40px_rgba(63,74,97,0.12),0_4px_12px_rgba(63,74,97,0.08)] border-[8px] border-solid border-white relative overflow-hidden">
                <ParentWarningTitle />

                <div className="flex-col-el flex-center pb-4">
                  <p className="text-body text-textcolor-black text-2xl text-center">
                    {I18n.t("MSG_UPGRADE_PROMOTION_TITLE")}
                  </p>
                </div>

                <div className="flex-col-el w-full h-full">
                  <div className="px-6 pb-2">
                    <div
                      className="bg-white overflow-hidden"
                      style={{
                        boxShadow:
                          "-1px 1px 1px rgba(63,74,97,0.10), 1px 1px 3px rgba(63,74,97,0.15)",
                      }}
                    >
                      {/* Plan Badge Row */}
                      <div
                        className="grid border-t border-b"
                        style={{
                          gridTemplateColumns: GRID_COLUMNS,
                          borderTopColor: "#E8DCC8",
                          borderBottomColor: "#E8DCC8",
                        }}
                      >
                        <div className="flex items-center gap-2 p-3 text text-textcolor-black">
                          <span className="material-symbols-outlined !text-xl text-gray2-100">
                            workspace_premium
                          </span>
                          <span className="text-xl">
                            {I18n.t("MSG_UPGRADE_PROMOTION_TABLE_PLAN")}
                          </span>
                        </div>
                        <div
                          className={`flex items-center justify-center p-3 cursor-pointer border-l-4 border-r-4 border-t-4 ${
                            displayPlan !== PlanType.TRIAL ? "hover:bg-gray-50" : ""
                          }`}
                          style={{
                            borderLeftColor:
                              displayPlan === PlanType.TRIAL
                                ? PLAN_COLORS[PlanType.TRIAL]
                                : "transparent",
                            borderRightColor:
                              displayPlan === PlanType.TRIAL
                                ? PLAN_COLORS[PlanType.TRIAL]
                                : "transparent",
                            borderTopColor:
                              displayPlan === PlanType.TRIAL
                                ? PLAN_COLORS[PlanType.TRIAL]
                                : "transparent",
                            backgroundColor:
                              displayPlan === PlanType.TRIAL
                                ? `${PLAN_COLORS[PlanType.TRIAL]}10`
                                : undefined,
                          }}
                          onClick={() => handlePlanClick(PlanType.TRIAL)}
                        >
                          <PlanBadge plan={PlanType.TRIAL} />
                        </div>
                        <div
                          className={`flex items-center justify-center p-3 cursor-pointer border-l-4 border-r-4 border-t-4 ${
                            displayPlan !== PlanType.FREE ? "hover:bg-gray-50" : ""
                          }`}
                          style={{
                            borderLeftColor:
                              displayPlan === PlanType.FREE
                                ? PLAN_COLORS[PlanType.FREE]
                                : "transparent",
                            borderRightColor:
                              displayPlan === PlanType.FREE
                                ? PLAN_COLORS[PlanType.FREE]
                                : "transparent",
                            borderTopColor:
                              displayPlan === PlanType.FREE
                                ? PLAN_COLORS[PlanType.FREE]
                                : "transparent",
                            backgroundColor:
                              displayPlan === PlanType.FREE
                                ? `${PLAN_COLORS[PlanType.FREE]}10`
                                : undefined,
                          }}
                          onClick={() => handlePlanClick(PlanType.FREE)}
                        >
                          <PlanBadge plan={PlanType.FREE} />
                        </div>
                        <div
                          className={`flex items-center justify-center p-3 cursor-pointer border-l-4 border-r-4 border-t-4 ${
                            displayPlan !== PlanType.STANDARD ? "hover:bg-gray-50" : ""
                          }`}
                          style={{
                            borderLeftColor:
                              displayPlan === PlanType.STANDARD
                                ? PLAN_COLORS[PlanType.STANDARD]
                                : "transparent",
                            borderRightColor:
                              displayPlan === PlanType.STANDARD
                                ? PLAN_COLORS[PlanType.STANDARD]
                                : "transparent",
                            borderTopColor:
                              displayPlan === PlanType.STANDARD
                                ? PLAN_COLORS[PlanType.STANDARD]
                                : "transparent",
                            backgroundColor:
                              displayPlan === PlanType.STANDARD
                                ? `${PLAN_COLORS[PlanType.STANDARD]}10`
                                : undefined,
                          }}
                          onClick={() => handlePlanClick(PlanType.STANDARD)}
                        >
                          <PlanBadge plan={PlanType.STANDARD} />
                        </div>
                        <div
                          className={`flex items-center justify-center p-3 cursor-pointer border-l-4 border-r-4 border-t-4 ${
                            displayPlan !== PlanType.PREMIUM ? "hover:bg-gray-50" : ""
                          }`}
                          style={{
                            borderLeftColor:
                              displayPlan === PlanType.PREMIUM
                                ? PLAN_COLORS[PlanType.PREMIUM]
                                : "transparent",
                            borderRightColor:
                              displayPlan === PlanType.PREMIUM
                                ? PLAN_COLORS[PlanType.PREMIUM]
                                : "transparent",
                            borderTopColor:
                              displayPlan === PlanType.PREMIUM
                                ? PLAN_COLORS[PlanType.PREMIUM]
                                : "transparent",
                            backgroundColor:
                              displayPlan === PlanType.PREMIUM
                                ? `${PLAN_COLORS[PlanType.PREMIUM]}10`
                                : undefined,
                          }}
                          onClick={() => handlePlanClick(PlanType.PREMIUM)}
                        >
                          <PlanBadge plan={PlanType.PREMIUM} />
                        </div>
                      </div>

                      {/* Comparison Rows */}
                      <ComparisonRow
                        icon="group"
                        label={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_USERS")}
                        trialValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_USERS_TRIAL")}
                        freeValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_USERS_FREE")}
                        standardValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_USERS_STANDARD")}
                        premiumValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_USERS_PREMIUM")}
                        displayPlan={displayPlan}
                        onPlanClick={handlePlanClick}
                      />
                      <ComparisonRow
                        icon="construction"
                        label={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_PROJECTS")}
                        trialValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_PROJECTS_TRIAL")}
                        freeValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_PROJECTS_FREE")}
                        standardValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_PROJECTS_STANDARD")}
                        premiumValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_PROJECTS_PREMIUM")}
                        displayPlan={displayPlan}
                        onPlanClick={handlePlanClick}
                      />
                      <ComparisonRow
                        icon="auto_awesome"
                        label={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_IMAGE")}
                        trialValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_IMAGE_TRIAL")}
                        freeValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_IMAGE_FREE")}
                        standardValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_IMAGE_STANDARD")}
                        premiumValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_IMAGE_PREMIUM")}
                        displayPlan={displayPlan}
                        onPlanClick={handlePlanClick}
                      />
                      <ComparisonRow
                        icon="chat"
                        label={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_CHAT")}
                        trialValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_CHAT_TRIAL")}
                        freeValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_CHAT_FREE")}
                        standardValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_CHAT_STANDARD")}
                        premiumValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_AI_CHAT_PREMIUM")}
                        displayPlan={displayPlan}
                        onPlanClick={handlePlanClick}
                      />
                      <ComparisonRow
                        icon="cloud_upload"
                        label={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_STORAGE")}
                        trialValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_STORAGE_TRIAL")}
                        freeValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_STORAGE_FREE")}
                        standardValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_STORAGE_STANDARD")}
                        premiumValue={I18n.t("MSG_UPGRADE_PROMOTION_TABLE_STORAGE_PREMIUM")}
                        displayPlan={displayPlan}
                        onPlanClick={handlePlanClick}
                      />

                      {/* Button Row */}
                      <div className="grid" style={{ gridTemplateColumns: GRID_COLUMNS }}>
                        <div className="p-3" />
                        {/* TRIAL列: 選択されていればハイライト */}
                        <div
                          className={`flex items-center justify-center p-3 cursor-pointer border-l-4 border-r-4 border-b-4 ${
                            displayPlan !== PlanType.TRIAL ? "hover:bg-gray-50" : ""
                          }`}
                          style={{
                            borderLeftColor:
                              displayPlan === PlanType.TRIAL
                                ? PLAN_COLORS[PlanType.TRIAL]
                                : "transparent",
                            borderRightColor:
                              displayPlan === PlanType.TRIAL
                                ? PLAN_COLORS[PlanType.TRIAL]
                                : "transparent",
                            borderBottomColor:
                              displayPlan === PlanType.TRIAL
                                ? PLAN_COLORS[PlanType.TRIAL]
                                : "transparent",
                            backgroundColor:
                              displayPlan === PlanType.TRIAL
                                ? `${PLAN_COLORS[PlanType.TRIAL]}10`
                                : undefined,
                          }}
                          onClick={() => handlePlanClick(PlanType.TRIAL)}
                        >
                          {plan === PlanType.TRIAL && (
                            <span
                              className="text text-base"
                              style={{ color: PLAN_COLORS[PlanType.TRIAL] }}
                            >
                              {I18n.t("MSG_CURRENT_PLAN")}
                            </span>
                          )}
                        </div>
                        {/* FREE列: プランに応じて表示を切り替え */}
                        <div
                          className={`flex items-center justify-center p-3 alpha cursor-pointer border-l-4 border-r-4 border-b-4 ${
                            displayPlan !== PlanType.FREE ? "hover:bg-gray-50" : ""
                          }`}
                          style={{
                            borderLeftColor:
                              displayPlan === PlanType.FREE
                                ? PLAN_COLORS[PlanType.FREE]
                                : "transparent",
                            borderRightColor:
                              displayPlan === PlanType.FREE
                                ? PLAN_COLORS[PlanType.FREE]
                                : "transparent",
                            borderBottomColor:
                              displayPlan === PlanType.FREE
                                ? PLAN_COLORS[PlanType.FREE]
                                : "transparent",
                            backgroundColor:
                              displayPlan === PlanType.FREE
                                ? `${PLAN_COLORS[PlanType.FREE]}10`
                                : undefined,
                          }}
                          onClick={() => handlePlanClick(PlanType.FREE)}
                        >
                          {plan === PlanType.TRIAL && (
                            <button
                              className="flex-row-el items-center justify-center gap-2 py-3 px-6 text-white rounded-[12px] text-xl border-none cursor-pointer hover:opacity-90 transition-all"
                              style={{ backgroundColor: PLAN_COLORS[PlanType.FREE] }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPlan(PlanType.FREE);
                                handleRegisterClick();
                              }}
                            >
                              <span className="material-symbols-outlined !text-3xl">sync_alt</span>
                              {I18n.t("MSG_UPGRADE_PROMOTION_CHANGE_BTN")}
                            </button>
                          )}
                          {plan === PlanType.FREE && (
                            <span
                              className="text text-base"
                              style={{ color: PLAN_COLORS[PlanType.FREE] }}
                            >
                              {I18n.t("MSG_CURRENT_PLAN")}
                            </span>
                          )}
                        </div>
                        {/* STANDARD列: プランに応じて表示を切り替え */}
                        <div
                          className={`flex items-center justify-center p-3 alpha cursor-pointer border-l-4 border-r-4 border-b-4 ${
                            displayPlan !== PlanType.STANDARD ? "hover:bg-gray-50" : ""
                          }`}
                          style={{
                            borderLeftColor:
                              displayPlan === PlanType.STANDARD
                                ? PLAN_COLORS[PlanType.STANDARD]
                                : "transparent",
                            borderRightColor:
                              displayPlan === PlanType.STANDARD
                                ? PLAN_COLORS[PlanType.STANDARD]
                                : "transparent",
                            borderBottomColor:
                              displayPlan === PlanType.STANDARD
                                ? PLAN_COLORS[PlanType.STANDARD]
                                : "transparent",
                            backgroundColor:
                              displayPlan === PlanType.STANDARD
                                ? `${PLAN_COLORS[PlanType.STANDARD]}10`
                                : undefined,
                          }}
                          onClick={() => handlePlanClick(PlanType.STANDARD)}
                        >
                          {(plan === PlanType.TRIAL || plan === PlanType.FREE) && (
                            <button
                              className="flex-row-el items-center justify-center gap-2 py-3 px-6 text-white rounded-[12px] text-xl border-none cursor-pointer hover:opacity-90 transition-all"
                              style={{ backgroundColor: PLAN_COLORS[PlanType.STANDARD] }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPlan(PlanType.STANDARD);
                                handlePurchaseClick();
                              }}
                            >
                              <span className="material-symbols-outlined !text-3xl">sync_alt</span>
                              {I18n.t("MSG_UPGRADE_PROMOTION_CHANGE_BTN")}
                            </button>
                          )}
                          {plan === PlanType.STANDARD && (
                            <span
                              className="text text-base"
                              style={{ color: PLAN_COLORS[PlanType.STANDARD] }}
                            >
                              {I18n.t("MSG_CURRENT_PLAN")}
                            </span>
                          )}
                        </div>
                        {/* PREMIUM列: プランに応じて表示を切り替え */}
                        <div
                          className={`flex items-center justify-center p-3 alpha cursor-pointer border-l-4 border-r-4 border-b-4 ${
                            displayPlan !== PlanType.PREMIUM ? "hover:bg-gray-50" : ""
                          }`}
                          style={{
                            borderLeftColor:
                              displayPlan === PlanType.PREMIUM
                                ? PLAN_COLORS[PlanType.PREMIUM]
                                : "transparent",
                            borderRightColor:
                              displayPlan === PlanType.PREMIUM
                                ? PLAN_COLORS[PlanType.PREMIUM]
                                : "transparent",
                            borderBottomColor:
                              displayPlan === PlanType.PREMIUM
                                ? PLAN_COLORS[PlanType.PREMIUM]
                                : "transparent",
                            backgroundColor:
                              displayPlan === PlanType.PREMIUM
                                ? `${PLAN_COLORS[PlanType.PREMIUM]}10`
                                : undefined,
                          }}
                          onClick={() => handlePlanClick(PlanType.PREMIUM)}
                        >
                          {plan !== PlanType.PREMIUM && (
                            <button
                              className="flex-row-el items-center justify-center gap-2 py-3 px-6 text-white rounded-[12px] text-xl border-none cursor-pointer hover:opacity-90 transition-all"
                              style={{ backgroundColor: PLAN_COLORS[PlanType.PREMIUM] }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPlan(PlanType.PREMIUM);
                                handlePurchaseClick();
                              }}
                            >
                              <span className="material-symbols-outlined !text-3xl">sync_alt</span>
                              {I18n.t("MSG_UPGRADE_PROMOTION_CHANGE_BTN")}
                            </button>
                          )}
                          {plan === PlanType.PREMIUM && (
                            <span
                              className="text text-base"
                              style={{ color: PLAN_COLORS[PlanType.PREMIUM] }}
                            >
                              {I18n.t("MSG_CURRENT_PLAN")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <ParentGate
        visible={parentGate}
        confirm={handleParentGateConfirm}
        close={handleParentGateClose}
      />

      <DialogMessage
        open={showLoginDialog}
        zIndex={9999999999}
        title={I18n.t("MSG_LOGIN_REQUIRED_TITLE")}
        message={I18n.t("MSG_LOGIN_REQUIRED_MESSAGE")}
        close={handleLoginDialogClose}
        confirm={handleLoginDialogConfirm}
        confirmBtnText={I18n.t("MSG_SIGN_UP_BTN")}
        confirmBtnIcon="person_add"
      />
    </>
  );
};
